// A2 lineup pre-pull. After computeFunStats returns, walk the high-value
// record entries (championships, top-10 single-game high, top-5 blowouts)
// and attach the actual starting lineups so click-to-expand renders
// instantly.
//
// Sleeper: zero extra fetches — Sleeper's /league/{id}/matchups/{week}
// endpoint already returns players_points/starters/starters_points and
// the import flow stores those rows verbatim in season.matchupsByWeek
// (audited Phase 0). Adapter just normalizes from memory.
//
// Yahoo: needs one /api/yahoo/lineup call per (team, week). The proxy
// hits /team/{key}/roster;week={N};stats=1, throttled at 1.1s by the
// shared rate-limit pattern. Smithtown estimate: ~35 games × 2 teams =
// ~80s. Surface a progress message so the user knows what's happening.
//
// Lazy-fetch path (any non-record game clicked post-load) lives in
// LineupView.jsx using lineupCache + the same adapters.

import { sleeperToLineup, yahooToLineup } from "./recapAdapters.js";
import { getSleeperPlayersMap } from "./sleeperImport.js";
import { fetchYahooLineup } from "./yahooImport.js";
import { setLineup } from "./lineupCache.js";

const YAHOO_THROTTLE_MS = 1100;

// Public entry point. Mutates stats — attaches `lineup` + `opponentLineup`
// to record entries that we can resolve. Best-effort: failures on
// individual games just leave the lineup off and the user can lazy-fetch.
export async function attachRecordLineups({
  stats,
  seasons,
  platform,
  leagueId,
  token,           // yahoo only
  aliasMap,        // sleeper canonical owner resolution
  onProgress,
}) {
  if (!stats || !Array.isArray(seasons) || !seasons.length) return;
  if (platform === "sleeper") {
    await attachSleeper({ stats, seasons, leagueId, aliasMap, onProgress });
  } else if (platform === "yahoo") {
    await attachYahoo({ stats, seasons, leagueId, token, aliasMap, onProgress });
  }
}

// ---------- Sleeper ----------

async function attachSleeper({ stats, seasons, leagueId, aliasMap, onProgress }) {
  onProgress?.("Pulling lineups for record games...", 0, 1);
  const playersMap = await getSleeperPlayersMap();
  // Build year -> season payload + roster_id -> canonical-owner index up
  // front so the per-record lookup is O(1).
  const byYear = {};
  for (const s of seasons) {
    const year = String(s.season);
    byYear[year] = {
      season: s,
      rosterIdToOwner: buildSleeperRosterMap(s, aliasMap || {}),
      ownerToRosterId: invertRosterMap(buildSleeperRosterMap(s, aliasMap || {})),
    };
  }

  const targets = collectTargets(stats);
  for (const t of targets) {
    const ctx = byYear[String(t.year)];
    if (!ctx) continue;
    const wkRows = ctx.season.matchupsByWeek?.[t.week];
    if (!Array.isArray(wkRows)) continue;
    const ownerRosterId = ctx.ownerToRosterId[t.owner];
    const oppRosterId = ctx.ownerToRosterId[t.opp];
    if (ownerRosterId == null || oppRosterId == null) continue;
    const ownerEntry = wkRows.find(r => r.roster_id === ownerRosterId);
    const oppEntry = wkRows.find(r => r.roster_id === oppRosterId);
    const lineup = sleeperToLineup(ownerEntry, playersMap, t.year);
    const opponentLineup = sleeperToLineup(oppEntry, playersMap, t.year);
    if (lineup) {
      assignLineup(t.target, lineup, opponentLineup);
      // Also seed the lazy-fetch cache so subsequent navigations hit
      // localStorage rather than recomputing.
      setLineup({ leagueId, year: String(t.year), week: t.week, ownerKey: String(ownerRosterId), lineup });
      if (opponentLineup) setLineup({ leagueId, year: String(t.year), week: t.week, ownerKey: String(oppRosterId), lineup: opponentLineup });
    }
  }
  onProgress?.("Lineups attached", 1, 1);
}

function buildSleeperRosterMap(season, aliasMap) {
  // Mirrors recapAdapters' buildRosterMap — we can't import it cleanly
  // (it's not exported) so we redo the lookup inline. Cheap, ~12 entries.
  const userCanonical = {};
  for (const u of season.users || []) {
    if (!u.user_id) continue;
    const raw = u.display_name || `User ${u.user_id}`;
    userCanonical[u.user_id] = aliasMap[raw] || raw;
  }
  const m = {};
  for (const r of season.rosters || []) {
    if (r.owner_id && userCanonical[r.owner_id]) {
      m[r.roster_id] = userCanonical[r.owner_id];
    } else {
      m[r.roster_id] = `Roster ${r.roster_id}`;
    }
  }
  return m;
}

function invertRosterMap(rosterIdToOwner) {
  const out = {};
  for (const [rid, owner] of Object.entries(rosterIdToOwner)) {
    out[owner] = Number(rid);
  }
  return out;
}

// ---------- Yahoo ----------

async function attachYahoo({ stats, seasons, leagueId, token, aliasMap, onProgress }) {
  if (!token) return; // no oauth, no lineups
  const byYear = {};
  for (const s of seasons) {
    byYear[String(s.season)] = {
      season: s,
      teamKeyToOwner: buildYahooTeamLookup(s, aliasMap || {}),
      ownerToTeamKey: invertTeamLookup(buildYahooTeamLookup(s, aliasMap || {})),
    };
  }

  const targets = collectTargets(stats);
  // Each target needs 2 fetches (winner + opponent). De-dupe across targets
  // since the same playoff game can appear in both championships and
  // single-game lists.
  const toFetch = new Map(); // key=`year|week|team_key` -> {year, week, teamKey, owner}
  for (const t of targets) {
    const ctx = byYear[String(t.year)];
    if (!ctx) continue;
    const ownerTeam = ctx.ownerToTeamKey[t.owner];
    const oppTeam = ctx.ownerToTeamKey[t.opp];
    if (ownerTeam) toFetch.set(`${t.year}|${t.week}|${ownerTeam}`, { year: t.year, week: t.week, teamKey: ownerTeam, owner: t.owner });
    if (oppTeam) toFetch.set(`${t.year}|${t.week}|${oppTeam}`, { year: t.year, week: t.week, teamKey: oppTeam, owner: t.opp });
  }

  const total = toFetch.size;
  // Always announce the phase change immediately — even when there's
  // nothing to fetch — so the UI bar can transition from the fetch
  // phase's terminal "All seasons loaded" (current === total, == 100%)
  // into the lineup phase rather than appearing frozen.
  onProgress?.(`Pulling lineups for record games...`, 0, Math.max(1, total));
  if (!total) {
    onProgress?.(`Lineups attached`, 1, 1);
    return;
  }
  let completed = 0;
  let lastDelay = 0;
  const cache = new Map(); // `year|week|owner` -> lineup
  for (const entry of toFetch.values()) {
    onProgress?.(`Pulling lineups for record games... (${completed}/${total})`, completed, total);
    await delayUntil(lastDelay);
    try {
      const resp = await fetchYahooLineup({ token, teamKey: entry.teamKey, week: entry.week });
      lastDelay = Date.now() + YAHOO_THROTTLE_MS;
      const lineup = yahooToLineup(resp, entry.year);
      if (lineup) {
        cache.set(`${entry.year}|${entry.week}|${entry.owner}`, lineup);
        setLineup({ leagueId, year: String(entry.year), week: entry.week, ownerKey: entry.teamKey, lineup });
      }
    } catch (e) {
      if (e?.code === "AUTH") throw e;
      // Non-auth errors swallowed - missing lineup just leaves the click
      // affordance to lazy-fetch.
    }
    completed++;
  }

  // Now stamp lineups onto record targets from the cache.
  for (const t of targets) {
    const winner = cache.get(`${t.year}|${t.week}|${t.owner}`);
    const loser = cache.get(`${t.year}|${t.week}|${t.opp}`);
    if (winner) assignLineup(t.target, winner, loser);
  }
}

function buildYahooTeamLookup(season, aliasMap) {
  const guidCanonical = {};
  for (const t of season.teams || []) {
    if (!t.guid) continue;
    const raw = t.nickname || `Manager ${String(t.guid).slice(0, 6)}`;
    guidCanonical[t.guid] = aliasMap[raw] || raw;
  }
  const m = {};
  for (const t of season.teams || []) {
    if (t.team_key && t.guid && guidCanonical[t.guid]) {
      m[t.team_key] = guidCanonical[t.guid];
    }
  }
  return m;
}

function invertTeamLookup(teamKeyToOwner) {
  const out = {};
  for (const [tk, owner] of Object.entries(teamKeyToOwner)) {
    out[owner] = tk;
  }
  return out;
}

// ---------- Shared ----------

// Walks the stats object and returns the list of records we want to pre-pull
// lineups for. Each entry: { year, week, owner, opp, target: <ref> }.
// `target` is the actual object we mutate to attach the lineup so the
// pre-pull can write directly without secondary lookups.
function collectTargets(stats) {
  const out = [];
  for (const c of stats.championships?.years || []) {
    if (c.champion && c.runnerUp && c.year && c.week) {
      out.push({ year: c.year, week: c.week, owner: c.champion, opp: c.runnerUp, target: c });
    }
  }
  const highs = (stats.singleGame?.highest || []).slice(0, 10);
  for (const r of highs) {
    if (r.year && r.week && r.owner && r.opp) {
      out.push({ year: r.year, week: r.week, owner: r.owner, opp: r.opp, target: r });
    }
  }
  const blowouts = (stats.singleGame?.biggestBlowouts || []).slice(0, 5);
  for (const r of blowouts) {
    if (r.year && r.week && r.owner && r.opp) {
      out.push({ year: r.year, week: r.week, owner: r.owner, opp: r.opp, target: r });
    }
  }
  return out;
}

function assignLineup(target, lineup, opponentLineup) {
  if (!target) return;
  target.lineup = lineup;
  if (opponentLineup) target.opponentLineup = opponentLineup;
}

async function delayUntil(ts) {
  const wait = ts - Date.now();
  if (wait > 0) await new Promise(r => setTimeout(r, wait));
}
